import { Heading, List, ListItem, Text, VStack } from '@chakra-ui/react';
import { FC, useEffect, useState } from 'react';
import { SuggestedMessage } from '@frx/frx-services';
import {
  useSuggestedMessage,
  useSuggestedMessages,
} from '@frx/frx-react-context';
import parse from 'html-react-parser';
import { useEventId, useIsMobile, useShareMessage } from '../../hooks';
import { Modal } from '../modal';
import { StepButton } from '../step-button';
import { SocialShare } from '../social-share';
import { Loader } from '../loader';
import { CopyButton } from '../copy-link';
import { getDataTracking } from '../../utils';

const decodeHtmlEntities = (value = ''): string => {
  const textarea = document.createElement('textarea');
  textarea.innerHTML = value;
  return textarea.value;
};

export interface ShareMessageProps {
  isOpen: boolean;
  onClose: () => void;
  personalPageUrl: string;
}

export const ShareMessages: FC<ShareMessageProps> = ({
  isOpen,
  onClose,
  personalPageUrl,
}) => {
  const isMobile = useIsMobile();
  const [messageId, setMessageId] = useState<number>();
  const [desktopView, setDesktopView] = useState(false);
  const eventId = useEventId();
  const { getShareMessageIcon, sanitizeHTML } = useShareMessage();
  const { data, isInitialLoading } = useSuggestedMessages({ fr_id: eventId });
  const { data: suggestedMessage, refetch } = useSuggestedMessage({
    fr_id: eventId,
    message_id: Number(messageId),
  });

  useEffect(() => {
    if (messageId) {
      refetch().then((r) => {
        const shareText = decodeHtmlEntities(
          sanitizeHTML(r.data?.messageInfo.messageBody)
        );

        if (
          isMobile &&
          navigator.share &&
          navigator.canShare({ title: r.data?.messageInfo.subject })
        ) {
          navigator
            .share({
              title: r.data?.messageInfo.subject,
              text: shareText,
            })
            .then(() => {
              console.log('Successfully shared');
            })
            .catch((error) => {
              console.error('Something went wrong sharing the blog', error);
            });
        } else {
          setDesktopView(true);
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile, refetch, messageId]);

  const handleClose = () => {
    onClose();
    setDesktopView(false);
    setMessageId(undefined);
  };

  const shareMessage = sanitizeHTML(suggestedMessage?.messageInfo.messageBody);

  const body = parse(shareMessage || '') as string;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      trackingLabel="shareMessageModal"
    >
      {!desktopView && (
        <VStack spacing={6} align="stretch">
          <VStack spacing={2}>
            <Heading
              fontSize="24px"
              color="brand.monochromatic.700"
              fontWeight="bold"
            >
              What kind of message would you like to share?
            </Heading>
            <Text fontSize="md">
              You’ll have an opportunity to edit your message and choose which
              app to send your message with.
            </Text>
          </VStack>
          {isInitialLoading && <Loader />}
          <List spacing={4}>
            {data?.suggestedMessage
              .filter(({ active }) => active)
              .map((message: SuggestedMessage) => {
                const IconName = getShareMessageIcon(message.name);
                return (
                  <ListItem key={`${message.messageId}-${message.name}`}>
                    <StepButton
                      {...getDataTracking(`${message.name}Message`)}
                      iconLeft={IconName}
                      label={message.name}
                      h="64px"
                      onClick={() => {
                        setMessageId(message.messageId);
                      }}
                    />
                  </ListItem>
                );
              })}
          </List>
        </VStack>
      )}
      {desktopView && (
        <VStack>
          <Heading
            fontSize="24px"
            color="brand.monochromatic.700"
            fontWeight="bold"
          >
            How would you like to share?
          </Heading>
          <SocialShare
            body={body}
            isEmailHasBody
            quote={body}
            trackingLabel="share message modal"
            url={personalPageUrl}
          />
          <CopyButton
            showLinkIcon={false}
            colorScheme="blue"
            text={body}
            {...getDataTracking(`share message modal copy button`)}
          >
            Copy Message
          </CopyButton>
        </VStack>
      )}
    </Modal>
  );
};
